import React, { useEffect, Suspense, useState } from 'react';
import Header from './components/Header';
import Hero from './components/sections/Hero';
import Services from './components/sections/Services';
import Workflow from './components/sections/Workflow';
import DockSection from './components/sections/DockSection';
import Footer from './components/Footer';
import ErrorBoundary from './components/ErrorBoundary';
import LoadingSpinner from './components/LoadingSpinner';
import { useScrollTracking } from './hooks/useScrollTracking';
import { trackPageView, initializeAnalytics } from './utils/analytics';
import Lenis from 'lenis';
import { motion, AnimatePresence } from 'framer-motion';
import { useRef } from 'react';

import './App.css';
import PrivacyPage from './pages/PrivacyPage';

// Extend Window interface to include Lenis
declare global {
  interface Window {
    lenis?: Lenis;
  }
}

// Lazy load pages to improve performance and prevent multiple Three.js instances
const ServicesPage = React.lazy(() => import('./pages/ServicesPage'));
const TeamsPage = React.lazy(() => import('./pages/TeamsPage'));
const ContactPage = React.lazy(() => import('./pages/ContactPage'));

function HomePage({ onNavigate, heroRef }: { onNavigate: (page: 'home' | 'services' | 'teams' | 'contact', anchor?: string) => void, heroRef: React.RefObject<HTMLElement> }) {
  return (
    <>
      <Header />
      <Hero onNavigate={onNavigate} sectionRef={heroRef} />
      <Services onNavigate={onNavigate} />
      <Workflow />
    </>
  );
}

function App() {
  const [currentPage, setCurrentPage] = useState<'home' | 'services' | 'teams' | 'contact' | 'privacy'>('home');
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [pendingAnchor, setPendingAnchor] = useState<string | null>(null);
  const [showDock, setShowDock] = useState(false);
  const heroSectionRef = useRef<HTMLElement>(null);

  // Initialize scroll tracking
  useScrollTracking();

  useEffect(() => {
    // Initialize Google Analytics
    initializeAnalytics();
    
    // Track initial page view
    trackPageView('home');

    // Initialize Lenis for smooth scrolling globally
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      touchMultiplier: 2,
      infinite: false,
      wheelMultiplier: 1,
      lerp: 0.1,
      syncTouch: true,
      syncTouchLerp: 0.075,
    });

    const raf = (time: number) => {
      lenis.raf(time);
      requestAnimationFrame(raf);
    };
    requestAnimationFrame(raf);

    window.lenis = lenis;

    // Ensure we start at the top
    setTimeout(() => {
      if (window.lenis) {
        window.lenis.scrollTo(0, { duration: 0.5 });
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 100);

    return () => {
      if (window.lenis) {
        window.lenis.destroy();
        window.lenis = undefined;
      }
    };
  }, []);

  // Effect to handle page changes and scroll to top
  useEffect(() => {
    // Scroll to top when page changes
    setTimeout(() => {
      if (window.lenis) {
        window.lenis.scrollTo(0, { duration: 0.5 });
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 100);
  }, [currentPage]);

  // Show DockSection only after scrolling on home page
  useEffect(() => {
    if (currentPage !== 'home') {
      setShowDock(true);
      return;
    }
    setShowDock(false);
    const handleScroll = () => {
      const hero = heroSectionRef.current;
      if (!hero) return;
      const rect = hero.getBoundingClientRect();
      // Show dock when bottom of hero is above half the viewport height
      const triggerPoint = window.innerHeight / 2;
      if (rect.bottom <= triggerPoint) setShowDock(true);
      else setShowDock(false);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [currentPage]);

  // Function to handle page navigation with smooth transitions
  const navigateToPage = async (page: 'home' | 'services' | 'teams' | 'contact' | 'privacy', anchor?: string) => {
    if (page === currentPage && !anchor) return;
    setIsPageLoading(true);
    // Scroll to top smoothly
    if (window.lenis) {
      window.lenis.scrollTo(0, { duration: 1 });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    // Small delay for smooth transition
    await new Promise(resolve => setTimeout(resolve, 300));
    setCurrentPage(page);
    trackPageView(page);
    if (anchor) setPendingAnchor(anchor);
    // Ensure we're at the top of the page
    setTimeout(() => {
      if (window.lenis) {
        window.lenis.scrollTo(0, { duration: 0.5 });
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      setIsPageLoading(false);
    }, 100);
  };

  // Effect to scroll to anchor after Teams page loads
  useEffect(() => {
    if (currentPage === 'teams' && pendingAnchor) {
      setTimeout(() => {
        const el = document.getElementById(pendingAnchor);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        setPendingAnchor(null);
      }, 400); // Wait for page transition and render
    }
  }, [currentPage, pendingAnchor]);

  return (
    <ErrorBoundary>
      <div className="App">
        {/* Page loading indicator */}
        {isPageLoading && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9998] flex items-center justify-center">
            <div className="text-center">
              <LoadingSpinner size="lg" color="white" className="mb-4" />
              <p className="text-white text-lg font-medium">Loading page...</p>
            </div>
          </div>
        )}

        <Suspense fallback={
          <div className="min-h-screen bg-black flex items-center justify-center">
            <div className="text-center">
              <LoadingSpinner size="lg" color="white" className="mb-4" />
              <p className="text-white text-lg font-medium">Loading...</p>
            </div>
          </div>
        }>
          <AnimatePresence mode="wait">
            {currentPage === 'home' ? (
              <motion.div
                key="home"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.5, ease: "easeInOut" }}
              >
                <HomePage onNavigate={navigateToPage} heroRef={heroSectionRef} />
              </motion.div>
            ) : currentPage === 'services' ? (
              <motion.div
                key="services"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.5, ease: "easeInOut" }}
              >
                <ServicesPage />
              </motion.div>
            ) : currentPage === 'teams' ? (
              <motion.div
                key="teams"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.5, ease: "easeInOut" }}
              >
                <TeamsPage />
              </motion.div>
            ) : currentPage === 'privacy' ? (
              <motion.div
                key="privacy"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.5, ease: "easeInOut" }}
              >
                <PrivacyPage />
              </motion.div>
            ) : (
              <motion.div
                key="contact"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.5, ease: "easeInOut" }}
              >
                <ContactPage />
              </motion.div>
            )}
          </AnimatePresence>
          
          <AnimatePresence>
            {showDock && (
              <motion.div
                key="dock"
                initial={{ opacity: 0, y: 60 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 60 }}
                transition={{ duration: 0.4, ease: "easeOut" }}
                style={{ position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 50 }}
              >
          <DockSection onNavigate={navigateToPage} />
              </motion.div>
            )}
          </AnimatePresence>
          <Footer onNavigate={navigateToPage} currentPage={currentPage} />
        </Suspense>
      </div>
    </ErrorBoundary>
  );
}

export default App; 